import React, { useState } from "react";
import { FooterSite, HeaderSite } from "@acme/feature";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { BiChevronDown, BiChevronUp, BiSearch, BiImage, BiMicrophone, BiCodeAlt, BiLayer, BiRocket } from "react-icons/bi";

export default function LandingPage() {
  return (
    <>
      <Head>
        <title>DapperGPT - Better Interface for any AI models</title>
        <meta
          name="description"
          content="Better AI interface for any model. Use OpenAI, Anthropic Claude, Google Gemini, Grok, Mistral, and Llama in one fast, beautiful UI with search, tools, agents, and file uploads."
        />
        <meta property="og:title" content="DapperGPT - Better UI Interface for any AI or LLM model" />
        <meta
          property="og:description"
          content="A modern UI for OpenAI, Claude, and Gemini—plus Grok, Mistral, and Llama. Built for speed, organization, and power users."
        />
      </Head>
      <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
        <HeaderSite containerClassName="!max-w-7xl px-6" />

        <main className="flex-grow">
          <HeroSection />
          <FeaturesGrid />
          <ModelSupportSection />
          <FaqSection />
        </main>

        <FooterSite />
      </div>
    </>
  );
};

const HeroSection = () => {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  return (
    <section className="relative pt-20 pb-32 overflow-hidden">
      {/* Background Gradient Blob */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-100/50 dark:bg-blue-900/20 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10 text-center">
        <div className="flex flex-col items-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300 text-sm font-medium mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            <span>v1.0 is now live</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 max-w-4xl mx-auto leading-tight pb-2">
            Better Interface <br /> for any AI or LLM model
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Experience the power of OpenAI, Anthropic, Gemini, and Grok models in one beautiful, minimalist interface. Rich chat, agents, tools, file uploads, and endless customization.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              href="/chat"
              className="px-8 py-4 bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl"
            >
              Start Chatting Free
            </Link>
            <Link
              href="/blog/introducing-dappergpt-1-0-rich-chat-multi-model"
              className="px-8 py-4 bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 rounded-lg font-semibold text-lg transition-all"
            >
              What&apos;s New
            </Link>
          </div>

          <div
            className="mt-20 relative w-full max-w-5xl mx-auto rounded-xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm group"
            onMouseEnter={() => setShouldLoadVideo(true)}
            onTouchStart={() => setShouldLoadVideo(true)}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent dark:from-gray-900 dark:via-transparent dark:to-transparent z-10 pointer-events-none" />
            <Image
              src="/landing/intro-fast.png"
              alt="DapperGPT Interface"
              width={1200}
              height={800}
              className="w-full h-auto opacity-100 dark:opacity-90 transition-opacity duration-300 group-hover:opacity-0"
              priority
            />
            {shouldLoadVideo && (
              <video
                src="/landing/intro-fast.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="none"
                className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-600 font-mono text-sm opacity-20 pointer-events-none">
              App Interface Preview
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ModelSupportSection = () => {
  return (
    <section className="py-24 border-t border-gray-100 dark:border-gray-900 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 text-center">
        <p className="text-gray-400 font-medium mb-12 uppercase tracking-[0.2em] text-xs">Integrates with powerful AI models</p>
        <div className="flex flex-wrap justify-center items-center gap-x-16 gap-y-10 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-500">
          <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">OpenAI</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-wide">Anthropic</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white font-sans">Gemini</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white font-mono">Mistral</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">Grok</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white font-serif">Llama</span>
        </div>
      </div>
    </section>
  )
}
const FeaturesGrid = () => {
  const features = [
    {
      title: "Multi-Model Support",
      description: "Switch between OpenAI GPT, Anthropic Claude, Gemini, Mistral, Grok, and Llama instantly.",
      icon: <BiLayer className="text-3xl" />,
      colSpan: "md:col-span-2",
      bg: "bg-purple-50 dark:bg-purple-900/10 border-purple-100 dark:border-purple-800/20"
    },
    {
      title: "Rich Chat Experience",
      description: "Upload images, docs, and interact with visual content seamlessly.",
      icon: <BiImage className="text-3xl" />,
      colSpan: "md:col-span-1",
      bg: "bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/20"
    },
    {
      title: "Smart Search",
      description: "Find any conversation or note with our Spotlight-like search.",
      icon: <BiSearch className="text-3xl" />,
      colSpan: "md:col-span-1",
      bg: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/20"
    },
    {
      title: "Tools and MCP",
      description: "Integrate with web browsing, code execution, and custom toolchains.",
      icon: <BiCodeAlt className="text-3xl" />,
      colSpan: "md:col-span-2",
      bg: "bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-800/20"
    },
    /* {
      title: "AI-Powered Notes",
      description: "Draft, edit, and organize your thoughts with AI assistance alongside your chat.",
      icon: <BiCodeAlt className="text-3xl" />,
      colSpan: "md:col-span-2",
      bg: "bg-orange-50 dark:bg-orange-900/10 border-orange-100 dark:border-orange-800/20"
    }, */
    {
      title: "Agents",
      description: "Speak naturally to your AI and hear human-like responses.",
      icon: <BiMicrophone className="text-3xl" />,
      colSpan: "md:col-span-1",
      bg: "bg-pink-50 dark:bg-pink-900/10 border-pink-100 dark:border-pink-800/20"
    },
    /* {
      title: "Voice Interaction",
      description: "Speak naturally to your AI and hear human-like responses.",
      icon: <BiMicrophone className="text-3xl" />,
      colSpan: "md:col-span-1",
      bg: "bg-pink-50 dark:bg-pink-900/10 border-pink-100 dark:border-pink-800/20"
    }, */
    {
      title: "Browser Extension",
      description: "Bring the power of DapperGPT to any website you visit. (available soon)",
      icon: <BiRocket className="text-3xl" />,
      colSpan: "md:col-span-2",
      bg: "bg-gray-50 dark:bg-gray-800/30 border-gray-100 dark:border-gray-700/30"
    },
    {
      title: "Many more features",
      description: "Custom prompts, folders, pinning, history, and so much more.",
      // icon: <BiRocket className="text-3xl" />,
      colSpan: "md:col-span-3",
      bg: "bg-gray-50 dark:bg-gray-800/30 border-gray-100 dark:border-gray-700/30"
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-16 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">Everything you need</h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Designed for power users who want more than just a chat box.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`p-8 rounded-2xl border backdrop-blur-sm hover:bg-opacity-80 transition-all duration-300 group ${feature.colSpan} ${feature.bg}`}
            >
              <div className="mb-4 text-gray-400 group-hover:text-gray-600 dark:text-gray-300 dark:group-hover:text-white transition-colors">{feature.icon}</div>
              <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{feature.title}</h3>
              <p className="text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-300 transition-colors">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqList = [
    {
      "question": "Is DapperGPT free?",
      "answer": "Yes, basic features are free. You just need your API Key from OpenAI, Anthropic, or Google to pay for usage."
    },
    {
      "question": "Which models are supported?",
      "answer": "We support major model providers and families across OpenAI, Anthropic (Claude), and Google (Gemini), plus others like Mistral, Grok, and Llama depending on your setup."
    },
    {
      "question": "Is my API key safe?",
      "answer": "Absolutely. Your API keys are encrypted and stored locally on your device. We do not proxy your requests."
    },
    {
      "question": "Can I sync across devices?",
      "answer": "Yes, create a DapperGPT account to securely sync your chats, folders, and prompts across all your devices."
    },
    {
      "question": "Can I bring my own API keys?",
      "answer": "Yes. DapperGPT is built for BYO keys so you can use your preferred provider and control costs."
    },
    {
      "question": "Do you store my chats on your servers?",
      "answer": "By default, your data stays on your device. If you enable sync, only the data required to sync is stored securely."
    },
    {
      "question": "Does DapperGPT support tools and agents?",
      "answer": "Yes. Use tools like browsing and custom workflows, and run multi-step agent tasks inside your workspace."
    },
    {
      "question": "Can I search my chat history?",
      "answer": "Yes. Search is built in so you can quickly find past conversations, prompts, and notes."
    },
    {
      "question": "Is there a UI for ChatGPT API / Claude API / Gemini API?",
      "answer": "Yes. Check our dedicated pages for a better ChatGPT UI, a free UI for ChatGPT API, a Claude API UI, and a Gemini API UI."
    }
  ]

  return (
    <section className="py-24 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white tracking-tight">Common Questions</h2>
        <div className="space-y-4">
          {faqList.map((item, idx) => (
            <div key={idx} className="border-b border-gray-100 dark:border-gray-800/50">
              <button
                onClick={() => toggle(idx)}
                className="w-full py-6 text-left flex items-center justify-between group"
              >
                <span className={`text-lg font-medium transition-colors ${openIndex === idx ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                  {item.question}
                </span>
                {openIndex === idx ? (
                  <BiChevronUp className="text-gray-400" />
                ) : (
                  <BiChevronDown className="text-gray-400" />
                )}
              </button>
              {openIndex === idx && (
                <div className="overflow-hidden">
                  <div className="pb-6 text-gray-500 dark:text-gray-400 leading-relaxed text-lg">
                    {item.answer}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
